import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Paper,
  Spinner,
  Alert,
  Chip,
  Tabs,
  EmptyState,
  Button,
  TextField,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import {
  adminApiClient,
  AdminApiError,
  type ProductDataReportItem,
  type ProductDataReportStatus,
} from '../lib/adminApiClient';

/**
 * "Report incorrect data" review queue (product:read to view, product:edit to
 * close). Shoppers flag a product's data from the inventory item page or the
 * product detail view; Rod 2026-09-13: "no email on this. just put it in the
 * queue for an admin to review". Fix the product in the editor, then resolve.
 * Reporters show by user id only.
 */

const FIELD_LABEL: Record<string, string> = {
  name: 'Name',
  brand: 'Brand',
  size: 'Size',
  price: 'Price',
  image: 'Image',
  category: 'Category',
  nutrition: 'Nutrition',
  ingredients: 'Ingredients',
  other: 'Other',
};

const NEUTRAL_BTN = { color: 'text.primary', borderColor: 'divider' };

export default function ProductDataReports() {
  const caps = useCapabilities();
  const canRead = caps.has('product:read');
  const canEdit = caps.has('product:edit');

  const [status, setStatus] = useState<ProductDataReportStatus>('open');
  const [items, setItems] = useState<ProductDataReportItem[]>([]);
  const [openCount, setOpenCount] = useState<number | null>(null);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listProductDataReports({ status });
      setItems(res.items);
      setOpenCount(res.openCount);
      setNextBefore(res.nextBefore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [status]);

  const loadMore = async () => {
    if (!nextBefore) return;
    setLoadingMore(true);
    try {
      const res = await adminApiClient.listProductDataReports({ status, before: nextBefore });
      setItems((prev) => [...prev, ...res.items]);
      setNextBefore(res.nextBefore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more reports');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (canRead) void load();
  }, [canRead, load]);

  if (caps.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }
  if (!canRead) {
    return <Alert severity="error">You don’t have permission to view product data reports.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Product data reports
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Shoppers flagged these products as having incorrect data. Fix the product in the editor, then resolve the
        report.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Tabs
          ariaLabel="Report status"
          value={status}
          onChange={(_e, v) => setStatus(v as ProductDataReportStatus)}
          tabs={[
            { value: 'open', label: openCount === null ? 'Open' : `Open (${openCount})` },
            { value: 'resolved', label: 'Resolved' },
            { value: 'dismissed', label: 'Dismissed' },
          ]}
        />
      </Box>

      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind === 'success' ? 'success' : 'error'}>{flash.msg}</Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner color="brand" />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState icon="checkCircleOutline" title="Nothing here" body={`No ${status} product data reports.`} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((row) => (
            <ReportRow
              key={row.reportId}
              row={row}
              canEdit={canEdit}
              onDone={(kind, msg) => {
                setFlash({ kind, msg });
                if (kind === 'success') void load();
              }}
            />
          ))}
          {nextBefore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button
                ariaLabel="Load more reports"
                buttonText={loadingMore ? 'Loading…' : 'Load more'}
                variant="outlined"
                size="small"
                sx={NEUTRAL_BTN}
                onClick={() => void loadMore()}
                disabled={loadingMore}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function ReportRow({
  row,
  canEdit,
  onDone,
}: {
  row: ProductDataReportItem;
  canEdit: boolean;
  onDone: (kind: 'success' | 'error', msg: string) => void;
}) {
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const title = [row.brand, row.productName].filter(Boolean).join(' ');

  const close = async (next: 'resolved' | 'dismissed') => {
    setBusy(true);
    try {
      await adminApiClient.closeProductDataReport(row.reportId, next, note.trim() || undefined);
      onDone('success', `${next === 'resolved' ? 'Resolved' : 'Dismissed'} the report on “${title}”.`);
    } catch (err) {
      onDone('error', err instanceof AdminApiError ? err.message : 'Failed to update report');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {row.thumbUrl && (
          <Box
            component="img"
            src={row.thumbUrl}
            alt=""
            sx={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 1, bgcolor: 'background.default' }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title || row.glpProductId}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {row.glpProductId}
            {row.storeName ? ` · seen at ${row.storeName}${row.storeCity ? `, ${row.storeCity}` : ''}` : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
            {row.fields.map((f) => (
              <Chip key={f} label={FIELD_LABEL[f] ?? f} color="warning" size="small" />
            ))}
          </Box>
          {row.note && (
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              “{row.note}”
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
            Reported {new Date(row.createdAt).toLocaleString()} · {row.source} · user {row.glpUserId ?? '(deleted)'}
          </Typography>
          {row.status !== 'open' && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
              {row.status === 'resolved' ? 'Resolved' : 'Dismissed'}
              {row.resolvedBy ? ` by ${row.resolvedBy}` : ''}
              {row.resolvedAt ? ` · ${new Date(row.resolvedAt).toLocaleDateString()}` : ''}
              {row.resolutionNote ? ` — ${row.resolutionNote}` : ''}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            ariaLabel={`Open ${title || row.glpProductId} in the product editor`}
            buttonText="Open in editor"
            variant="outlined"
            size="small"
            sx={NEUTRAL_BTN}
            onClick={() => navigate(`/product-editor/${encodeURIComponent(row.glpProductId)}`)}
          />
        </Box>
      </Box>

      {canEdit && row.status === 'open' && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            label="Resolution note (optional)"
            ariaLabel="Resolution note"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 500))}
            disabled={busy}
            fullWidth
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              ariaLabel="Dismiss report"
              buttonText="Dismiss"
              variant="outlined"
              size="small"
              sx={NEUTRAL_BTN}
              onClick={() => void close('dismissed')}
              disabled={busy}
            />
            <Button
              ariaLabel="Mark report resolved"
              buttonText="Resolve"
              variant="contained"
              size="small"
              onClick={() => void close('resolved')}
              disabled={busy}
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
}
